import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Spinner } from 'react-bootstrap';
import { BsTicketDetailed, BsExclamationCircle } from 'react-icons/bs';
import { baseURL } from '../../../../helpers/baseURL';
import { headers } from '../../../../helpers/Requests';
import { createMarkup } from '../../../../helpers/generic';
import styles from './TicketSidePanel.module.css';
import ColorBullet from '../../../ColorBullet';

const PER_PAGE = 20;

export default function TicketSidePanel({ currentTicketId, onSelect, onClose, userId, updatedTicket }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);       // full-panel spinner (initial / search)
    const [loadingMore, setLoadingMore] = useState(false); // bottom spinner (infinite scroll)
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const listRef = useRef(null);
    const searchTimer = useRef(null);
    const isFetching = useRef(false);          // ref-based guard — never stale in scroll handler
    const [hasMore, setHasMore] = useState(true);
    const [tab, setTab] = useState('mine'); // 'all' | 'mine'

    // Scroll list to top only when search changes (not on infinite scroll page bumps)
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [search]);

    // Scroll active item into view only when the selected ticket changes (not on append loads)
    useEffect(() => {
        if (listRef.current && currentTicketId) {
            const active = listRef.current.querySelector(`[data-id="${currentTicketId}"]`);
            if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [currentTicketId]);

    useEffect(() => {
        // "My Tickets" needs userId; on refresh/direct link user may not be loaded yet
        if (tab === 'mine' && !userId) {
            setLoading(true);
            return;
        }

        setTickets([]);
        setPage(1);
        setHasMore(true);
        isFetching.current = false;
        fetchTickets(1, search, false);
    }, [search, tab, userId]);

    // When TicketDetails updates a ticket, patch it in our local list immediately
    useEffect(() => {
        if (!updatedTicket?.id) return;
        const statusName = (updatedTicket.status?.name || '').toLowerCase();
        if (['closed', 'rejected'].includes(statusName)) {
            setTickets(prev => prev.filter(t => String(t.id) !== String(updatedTicket.id)));
            return;
        }
        setTickets(prev =>
            prev.map(t =>
                String(t.id) === String(updatedTicket.id)
                    ? { ...t, ...updatedTicket }
                    : t
            )
        );
    }, [updatedTicket]);

    const fetchTickets = async (p, q, append = false) => {
        if (isFetching.current) return;
        isFetching.current = true;
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const params = new URLSearchParams({
                per_page: PER_PAGE,
                page: p,
                sort_by: 'created_at',
                sort_direction: 'desc',
                ...(q ? { text: q } : {}),
            });

            if (tab === 'mine' && userId) {
                params.append('assignee', userId);
                params.append('created_by', userId);
            }

            const res = await axios.get(`${baseURL}tickets?${params}`, headers);
            const data = res?.data?.tickets ?? res?.data?.data ?? [];

            const newTickets = Array.isArray(data) ? data : [];

            setTickets(prev =>
                append ? [...prev, ...newTickets] : newTickets
            );

            setTotal(res?.data?.total ?? 0);

            // If fewer items returned than PER_PAGE → no more pages
            setHasMore(newTickets.length === PER_PAGE);
        } catch (err) {
            console.error('TicketSidePanel: failed to load tickets', err);
            if (!append) {
                setTickets([]);
                setTotal(0);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
            isFetching.current = false;
        }
    };

    const handleScroll = () => {
        const el = listRef.current;
        if (!el || isFetching.current || !hasMore) return;

        const threshold = 100; // px before bottom
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchTickets(nextPage, search, true);
        }
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            setSearch(val);
        }, 350);
    };

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <span className={styles.headerTitle}>
                    <BsTicketDetailed size={14} className="me-1" />
                    Tickets
                    {!loading && total > 0 && (
                        <span className={styles.totalBadge}>{total}</span>
                    )}
                </span>
            </div>

            <div className={styles.tabs}>
                <button
                    className={tab === 'mine' ? styles.tabActive : styles.tab}
                    onClick={() => setTab('mine')}
                >
                    My Tickets
                </button>

                <button
                    className={tab === 'all' ? styles.tabActive : styles.tab}
                    onClick={() => setTab('all')}
                >
                    All Tickets
                </button>
            </div>

            <div className={styles.searchBox}>
                <input
                    className={styles.searchInput}
                    placeholder="Search tickets…"
                    value={searchInput}
                    onChange={handleSearchChange}
                />
            </div>

            <div className={styles.list} ref={listRef} onScroll={handleScroll}>
                {loading ? (
                    <div className={styles.loadingWrap}>
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : tickets.length === 0 ? (
                    <div className={styles.empty}>
                        <BsExclamationCircle size={20} className="mb-1" />
                        <span>No tickets found</span>
                    </div>
                ) : (
                    <>
                        {tickets.map(t => {
                            const isActive = String(t.id) === String(currentTicketId);
                            const title = t.title || t.subject || `Ticket #${t.id}`;
                            const status = t.status?.name || t.status || '';
                            const priority = t.priority?.name || t.priority || '';
                            const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
                            return (
                                <div
                                    key={t.id}
                                    data-id={t.id}
                                    className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                                    onClick={() => !isActive && onSelect(t.id)}
                                    title={title}
                                >
                                    <span className={styles.itemId}>#{t.id}</span>
                                    <span className={styles.itemTitle}>{title}</span>
                                    {t.description && (
                                        <span className={styles.itemDescription} dangerouslySetInnerHTML={createMarkup(t.description)} />
                                    )}
                                    <div className={styles.itemMeta}>
                                        {status && (
                                            <span className={styles.chip}>
                                                <ColorBullet color={t.status?.color} size={8} />
                                                {capitalize(status)}
                                            </span>
                                        )}
                                        {priority && (
                                            <span className={styles.chip}>
                                                <ColorBullet color={t.priority?.color} size={8} />
                                                {capitalize(priority)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {loadingMore && (
                            <div className={styles.loadingWrap}>
                                <Spinner animation="border" size="sm" />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
